import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import type { Player } from '@/types/player';
import { PlayerPhoto } from './PlayerPhoto';

interface PlayerSearchProps {
  searchQuery: string;
  searchResults: Player[];
  isSearching: boolean;
  positionFilter: string | null;
  showPositionSearch: boolean;
  onSearchChange: (query: string) => void;
  onAddPlayer: (player: Player) => void;
  onClearPositionFilter: () => void;
}

export interface PlayerSearchRef {
  focusSearch: () => void;
  scrollToSearch: () => void;
}

export const PlayerSearch = forwardRef<PlayerSearchRef, PlayerSearchProps>((
  {
    searchQuery,
    searchResults,
    isSearching,
    positionFilter,
    onSearchChange,
    onAddPlayer,
    onClearPositionFilter
  },
  ref
) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    focusSearch: () => {
      inputRef.current?.focus();
    },
    scrollToSearch: () => {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }));

  const filterLabel = positionFilter === 'DEF' ? 'D/ST' : positionFilter;
  const showResults = searchQuery.length >= 2 || Boolean(positionFilter);

  return (
    <div ref={containerRef} className="relative">
      {positionFilter ? (
        <div className="mb-2 flex items-center gap-2 text-sm text-zinc-400">
          <span>Looking for {filterLabel}</span>
          <button
            type="button"
            onClick={onClearPositionFilter}
            className="text-zinc-500 hover:text-white"
            title="Clear position filter"
          >
            Clear
          </button>
        </div>
      ) : null}

      <input
        ref={inputRef}
        type="text"
        placeholder={filterLabel ? `Search ${filterLabel} players` : 'Search a player to add'}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className="w-full rounded-xl border border-white/20 bg-black/40 px-4 py-2.5 text-sm text-white placeholder:text-zinc-400 focus:border-fiesta-blue/60 focus:outline-none"
        autoComplete="off"
      />

      {showResults ? (
        <div className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-xl border border-white/10 bg-ink shadow-xl">
          {isSearching ? (
            <p className="px-4 py-3 text-sm text-zinc-500">Searching</p>
          ) : searchResults.length > 0 ? (
            searchResults.map((player) => (
              <button
                key={String(player.id)}
                type="button"
                onClick={() => onAddPlayer(player)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-white/5"
              >
                <PlayerPhoto
                  name={player.name}
                  position={player.position}
                  team={player.team}
                  sleeperId={player.id}
                  espnId={player.espn_id}
                  imageUrl={player.imageUrl}
                  className="h-7 w-7 rounded-full object-cover"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-white">{player.name}</span>
                  <span className="block text-xs text-zinc-500">
                    {player.position === 'DEF' ? 'D/ST' : player.position} · {player.team}
                  </span>
                </span>
              </button>
            ))
          ) : (
            <p className="px-4 py-3 text-sm text-zinc-500">No players found</p>
          )}
        </div>
      ) : null}
    </div>
  );
});

PlayerSearch.displayName = 'PlayerSearch';
